import { List as ImmutableList } from 'immutable';
import uniqBy from 'lodash/uniqBy';
import { DateTime, Interval } from 'luxon';

import { getInvoiceStatus } from 'app-components/flux/transactions/invoicesSelectors';

import type { Category } from '../categories/categoriesTypes';
import type {
  SplitItemProps,
  TransactionItem,
  TransactionProps,
  TransactionRecord,
} from '../transactions/transactionsTypes';
import type {
  CategoriesById,
  ReportDatePeriod,
  ReportGrouping,
  ReportSpecificFilter,
  SortedReportGrouping,
  TagsById,
} from './reportsTypes';

type FilterTransaction = TransactionProps | SplitItemProps;
type RecursiveReportFilter = (txn: FilterTransaction, categoriesById: CategoriesById) => boolean | undefined;

type GroupingKeys = {
  groupKeys: Array<string | undefined>;
  subKey: string | null | undefined;
  parentCategoryId?: string;
  subCategoryId?: string;
};

const getSplitItems = (txn: FilterTransaction): ImmutableList<SplitItemProps> | undefined => {
  const items = 'split' in txn ? txn.split?.items : undefined;
  return items && !Array.isArray(items) ? items : undefined;
};

export const getTimestepFormatting = (timeStep: string | null | undefined): 'day' | 'year' | 'month' => {
  switch (timeStep) {
    case 'days':
      return 'day';
    case 'years':
      return 'year';
    default:
      return 'month';
  }
};
const getISOMask = (timeStamp: string, timeStep: string): string => {
  switch (timeStep) {
    case 'days':
      return timeStamp.slice(0, 10);
    case 'years':
      return timeStamp.slice(0, 4);
    default:
      return timeStamp.slice(0, 7);
  }
};

export const constructDatePeriods = (
  dateRange: Interval | null | undefined,
  timeStep: string | null | undefined,
): ImmutableList<ReportDatePeriod> => {
  if (!dateRange || !timeStep) return ImmutableList<ReportDatePeriod>();
  const { start, end: endDate } = dateRange;
  if (!start || !endDate) throw new TypeError('Invalid report date range');

  const timeStepWithoutS = getTimestepFormatting(timeStep);

  let dateTicker = start.endOf(timeStepWithoutS);

  const dateTimeIncrement = { [timeStep]: 1 };
  return ImmutableList<ReportDatePeriod>().withMutations((boi) => {
    //  Retain the first dates real date
    const firstPeriodTimeStamp = dateTicker.toISO();
    if (!firstPeriodTimeStamp) throw new TypeError('Invalid report period');
    const firstPeriod = {
      timeStamp: firstPeriodTimeStamp,
      total: null,
      isoMask: getISOMask(firstPeriodTimeStamp, timeStep),
    };
    boi.push(firstPeriod);
    dateTicker = dateTicker.plus(dateTimeIncrement);

    //  All subsequent dates in between will always be the END of their time period
    while (dateTicker.startOf(timeStepWithoutS) <= endDate.startOf(timeStepWithoutS)) {
      const timeStamp =
        dateTicker.plus(dateTimeIncrement).startOf(timeStepWithoutS) > endDate.startOf(timeStepWithoutS)
          ? endDate.toISO()
          : dateTicker.endOf(timeStepWithoutS).toISO(); // Retain the last dates real date
      if (!timeStamp) throw new TypeError('Invalid report period');
      // These fields get treated as account names if you don't filter them out in getSavingsReportForDisplay
      const period = {
        timeStamp,
        total: null,
        isoMask: getISOMask(timeStamp, timeStep),
      };
      boi.push(period);
      dateTicker = dateTicker.plus(dateTimeIncrement);
    }
  });
};

export const getGroupingKeys = (
  txn: TransactionRecord,
  split: FilterTransaction,
  groupBy: string | null | undefined,
  categoriesById: CategoriesById,
  tagsById: TagsById,
): GroupingKeys => {
  const getRootCategory = (
    category: Category | undefined,
    visited: Set<string | undefined> = new Set(),
  ): Category | undefined => {
    if (!category) return category;

    if (!category.parentId || category.parentId === '0' || category.id === category.parentId) {
      return category;
    }
    if (visited.has(category.id)) {
      assert(false, 'Circular dependency detected for category', { id: category.id });
      return category;
    }
    visited.add(category.id);
    return getRootCategory(categoriesById.get(category.parentId), visited);
  };

  switch (groupBy) {
    case 'categories':
    case 'category': {
      const txnItem = txn.split ? split : txn;
      const txnCat = txnItem.coa?.type === 'CATEGORY' ? categoriesById.get(txnItem.coa.id) : null; //hmm... category reports exclude CreditCardPayment or other non-category designations

      if (txnCat) {
        const rootCat = getRootCategory(txnCat); // traverse the tree to the root node
        const parentCat = txnCat.parentId !== undefined ? categoriesById.get(txnCat.parentId) : undefined;
        return {
          groupKeys: [rootCat?.name],
          subKey: txnCat.name,
          parentCategoryId: parentCat?.id || txnCat.id, // used for deep linking
          subCategoryId: txnCat.id, // used for deep linking
        };
      }

      return { groupKeys: ['Uncategorized'], subKey: null, parentCategoryId: '0' }; // not categorized
    }
    case 'payees':
    case 'payee': {
      return {
        groupKeys: [txn.payee || 'no payee'],
        subKey: null,
      };
    }
    case 'tags':
    case 'tag': {
      const tagGroupKeys =
        split.tags && !Array.isArray(split.tags)
          ? split.tags
              .map((tag) => (tag.id !== undefined ? tagsById.get(tag.id)?.name : undefined) || null)
              .filter((val): val is string => val !== null)
              .toArray()
          : undefined;
      return {
        groupKeys: tagGroupKeys && tagGroupKeys.length > 0 ? tagGroupKeys : ['Untagged'],
        // groupKey: txn.tags?.reduce<string>((accum, curr) => `${accum}${accum.length > 0 ? '/' : ''}${tagsById.get(curr.get('id')).get('name')}`, '') || 'Untagged',
        subKey: null,
      };
    }
    default: {
      return { groupKeys: ['total'], subKey: null };
    }
  }
};

// helpers for getReportSpecificFilterFromReportName, returning true means keep
const spendingFilter: RecursiveReportFilter = (txn, categoriesById) => {
  const splitItems = getSplitItems(txn);
  if (splitItems && splitItems.size > 0) {
    return splitItems.some((split) => Boolean(spendingFilter(split, categoriesById)));
  }
  if (!txn.coa && !('split' in txn && txn.split?.items)) {
    return false;
  }
  if (txn.coa?.type && txn.coa.type === 'UNCATEGORIZED') {
    return true;
  }
  const category = txn.coa ? categoriesById.get(txn.coa.id) : undefined;

  return category && category.type === 'EXPENSE';
};
const incomeFilter: RecursiveReportFilter = (txn, categoriesById) => {
  if (txn.type === 'INVOICE') {
    return getInvoiceStatus(txn) !== 'draft';
  }
  const splitItems = getSplitItems(txn);
  if (splitItems && splitItems.size > 0) {
    return splitItems.some((split) => Boolean(incomeFilter(split, categoriesById)));
  }
  if (!txn.coa) {
    return false;
  }
  const category = categoriesById.get(txn.coa.id);
  return category && category.type === 'INCOME';
};
const noUpcomingFilter = (txn: TransactionRecord): boolean => txn.source !== 'SCHEDULED_TRANSACTION_PENDING';

const keepAll = (): boolean => true;

const isBusinessUsage = (txn: FilterTransaction): boolean =>
  !!txn.businessId && txn.businessId !== 'PERSONAL' && txn.businessId !== '0';

// Multi-split parents carry businessId=undefined and the actual usage lives on split items,
// so recurse — otherwise getReportFilteredTxns drops the txn before getOverTimeData iterates splits.
const businessIncomeFilter: RecursiveReportFilter = (txn, categoriesById) => {
  const splitItems = getSplitItems(txn);
  if (splitItems && splitItems.size > 0) {
    return splitItems.some((split) => Boolean(businessIncomeFilter(split, categoriesById)));
  }
  return isBusinessUsage(txn) && incomeFilter(txn, categoriesById);
};

const businessSpendingFilter: RecursiveReportFilter = (txn, categoriesById) => {
  const splitItems = getSplitItems(txn);
  if (splitItems && splitItems.size > 0) {
    return splitItems.some((split) => Boolean(businessSpendingFilter(split, categoriesById)));
  }
  return isBusinessUsage(txn) && spendingFilter(txn, categoriesById);
};

export function getReportSpecificFilterFromReportName(reportName: string | null | undefined): ReportSpecificFilter {
  switch (reportName) {
    case 'total-spending':
    case 'spending-dashboard-breakdown':
      return spendingFilter;
    case 'income':
    case 'spending-dashboard-flow-income':
      return incomeFilter;
    case 'business-income':
      return businessIncomeFilter;
    case 'business-spending':
      return businessSpendingFilter;
    case 'net-worth':
    case 'net-worth-page':
    case 'savings':
      return noUpcomingFilter;
    default:
      return keepAll;
  }
}

// takes a grouping object from getTotalData and converts subgroups into sorted lists
export function convertAndSortGrouping(
  groupObject: Record<string, ReportGrouping> | ReportGrouping[],
  sign: number,
): SortedReportGrouping[] {
  return Object.entries(groupObject)
    .sort(([_a, a], [_b, b]) => sign * b.total - sign * a.total)
    .map(([_g, group]) => {
      const convertedSubgroup = convertAndSortGrouping(group.subgroups || {}, sign);
      return {
        ...group,
        subgroups: convertedSubgroup,
        total: sign * group.total,
      };
    });
}

// expects a list of groups with lists of subgroups and caps them to a specific length
export function capGrouping(
  uncappedGrouping: SortedReportGrouping[],
  cap: number,
  overflowGroupName: string,
  filter?: boolean,
): [SortedReportGrouping[], TransactionRecord[], number, TransactionItem[]] {
  const cappedGrouping: SortedReportGrouping[] = [];
  const txns: TransactionRecord[] = [];
  const txnItems: TransactionItem[] = [];
  let total = 0;

  if (uncappedGrouping.length <= cap) {
    uncappedGrouping.forEach((group) => {
      if (!group.isIgnored && filter) {
        total += group.total;
        cappedGrouping.push(group);
      } else if (!filter) {
        total += group.total;
        cappedGrouping.push(group);
      }
      const txnsToPush = group.txns || [];
      txns.push(...txnsToPush);
      txnItems.push(...(group.txnItems || []));
    });
  } else {
    // here we need to do some work
    for (let i = 0; i < cap - 1; i++) {
      // first go through and add groups that aren't changing
      const group = uncappedGrouping[i];
      if (!group.isIgnored && filter) {
        total += group.total;
        cappedGrouping.push(group);
      } else if (!filter) {
        total += group.total;
        cappedGrouping.push(group);
      }
      const txnsToPush = group.txns || [];
      txns.push(...txnsToPush);
      txnItems.push(...(group.txnItems || []));
    }
    // add in the overflow group
    const [subGroup, subTxns, subTotal, subTxnItems] = capGrouping(
      uncappedGrouping.slice(cap - 1),
      cap,
      overflowGroupName,
      filter,
    ); //  Beautiful recursion
    total += subTotal;
    txns.push(...subTxns);
    txnItems.push(...subTxnItems);
    cappedGrouping.push({
      groupKey: overflowGroupName,
      total: subTotal,
      txns: subTxns,
      txnItems: subTxnItems,
      subgroups: subGroup,
    });
  }

  // when groups are created in the selector 'getTotalData' a txn can technically end up in multiple groups because
  //  of splits and multiple tags, this iterates over and removes the duplicates by id
  // TODO: do we need to adjust the total tho?
  const uniqueTxns = uniqBy(txns, (e) => e.id);

  return [cappedGrouping, uniqueTxns, total, txnItems];
}

export function adjustDateRangeEndToToday(dateRange: Interval): Interval {
  const { start, end } = dateRange;
  if (!start || !end) throw new TypeError('Invalid report date range');
  if (end.startOf('day').toISODate() === DateTime.local().startOf('day').toISODate()) {
    return dateRange;
  }
  return Interval.fromDateTimes(start, DateTime.local().endOf('day'));
}

const groupByToPlural: Record<string, string> = {
  category: 'categories',
  payee: 'payees',
  tag: 'tags',
  none: '',
};

export function makeGroupByPlural(groupBy: string | null | undefined): string {
  return (groupBy && groupByToPlural[groupBy]) || '';
}
